import axios from "axios";
import {
    clearStoredSession,
    isSessionExpired,
    readSelectedPharmacy,
    readStoredAuth,
    shouldRefreshSession,
    toSessionProfile,
    writeStoredAuth,
} from "../auth/authStorage";

// Session par cookie HttpOnly (SEC-008) : le navigateur envoie le cookie, le JavaScript ne voit jamais le JWT.
// L'en-tête X-Requested-With est exigé par le serveur pour toute écriture authentifiée par cookie (anti-CSRF).
export const API = axios.create({
    baseURL: process.env.REACT_APP_API_URL || `/api`,
    withCredentials: true,
    headers: { "X-Requested-With": "XMLHttpRequest" },
});

let redirectingToLogin = false;
let refreshPromise = null;

function isLoginRequest(config) {
    return String(config?.url || "").includes("/auth/login");
}

function isRefreshRequest(config) {
    return String(config?.url || "").includes("/auth/refresh");
}

function isLogoutRequest(config) {
    return String(config?.url || "").includes("/auth/logout");
}

function redirectToLoginOnce() {
    if (redirectingToLogin || globalThis.location?.pathname === "/login") return;
    redirectingToLogin = true;
    globalThis.location.href = "/login";
}

async function refreshAuth(auth) {
    if (!refreshPromise) {
        refreshPromise = API.post("/auth/refresh", null, { noRedirectOn401: true })
            .then(({ data }) => {
                const refreshedAuth = toSessionProfile({ ...auth, ...data });
                writeStoredAuth(refreshedAuth);
                return refreshedAuth;
            })
            .finally(() => {
                refreshPromise = null;
            });
    }
    return refreshPromise;
}

function readAuthSafely() {
    try {
        return readStoredAuth({ allowExpired: true });
    } catch {
        return null;
    }
}

function rejectExpiredRequest(config, message) {
    clearStoredSession({ expired: true });
    if (!config.noRedirectOn401) redirectToLoginOnce();
    return Promise.reject(new axios.CanceledError(message));
}

async function refreshAuthIfNeeded(auth, config) {
    if (!shouldRefreshSession(auth)) return auth;
    try {
        return await refreshAuth(auth);
    } catch (error) {
        if (error.response?.status !== 401) return auth;
        return rejectExpiredRequest(config, "Session refresh failed");
    }
}

function attachPharmacyHeader(config, auth) {
    if (auth.role !== "SUPER_ADMIN") return;
    const pharmacy = readSelectedPharmacy();
    if (pharmacy?.id) config.headers["X-Pharmacy-Id"] = pharmacy.id;
}

API.interceptors.request.use(async config => {
    if (isLoginRequest(config) || isRefreshRequest(config) || isLogoutRequest(config)) return config;
    let auth = readAuthSafely();
    if (!auth) return config;
    if (isSessionExpired(auth)) {
        return rejectExpiredRequest(config, "Session expired before request");
    }
    auth = await refreshAuthIfNeeded(auth, config);
    attachPharmacyHeader(config, auth);
    return config;
});

API.interceptors.response.use(
    response => response,
    error => {
        const config = error.config || {};
        if (error.response?.status === 401 && !config.noRedirectOn401 && !isLoginRequest(config)) {
            clearStoredSession({ expired: true });
            redirectToLoginOnce();
        }
        return Promise.reject(error);
    }
);

/** Demande au serveur d'effacer le cookie de session HttpOnly (illisible et ineffaçable côté JavaScript). */
export function logoutSession() {
    return API.post("/auth/logout", null, { noRedirectOn401: true });
}

/** Jeton court (2 min) pour le CONNECT WebSocket, gardé en mémoire seulement ; null sans session. */
export async function fetchWebSocketToken() {
    if (!readAuthSafely()) return null;
    const { data } = await API.post("/auth/ws-token", null, { noRedirectOn401: true });
    return data?.token ?? null;
}

export function createCommande(data) {
    return API.post("/commandes", data);
}

export function searchMedicamentReference(q) {
    return API.get("/medicaments-reference/search", { params: { q } });
}

export function listTherapeuticBookProducts() {
    return API.get("/therapeutic-book/products", { noRedirectOn401: true });
}

export function createTherapeuticBookProduct(data) {
    return API.post("/therapeutic-book/products", data, { noRedirectOn401: true });
}

export function deleteTherapeuticBookProduct(id) {
    return API.delete(`/therapeutic-book/products/${id}`, { noRedirectOn401: true });
}

export function suggestPatients(q, ehpadId) {
    return API.get("/patients/suggest", { params: { q, ehpadId } });
}

export function suggestEmails(q) {
    return API.get("/ehpads/emails/suggest", { params: { q } });
}

export function sendToEhpad(commandeId, sujet, contenu, recipients, attachments, idempotencyKey) {
    const form = new FormData();
    form.append("sujet", sujet);
    form.append("contenu", contenu);
    recipients.forEach(r => form.append("recipients", r));
    if (idempotencyKey) form.append("idempotencyKey", idempotencyKey);
    if (attachments) {
        attachments.forEach(f => form.append("attachments", f));
    }
    return API.post(`/commandes/${commandeId}/send-to-ehpad`, form, { noRedirectOn401: true });
}

export function sendPortalMessage(commandeId, content, idempotencyKey) {
    return API.post(
        `/commandes/${commandeId}/portal-message`,
        { content, idempotencyKey },
        { noRedirectOn401: true }
    );
}

export function deleteCommande(commandeId) {
    return API.delete(`/commandes/${commandeId}`);
}

export function assignEhpad(commandeId, ehpadId) {
    return API.patch(`/commandes/${commandeId}/ehpad`, { ehpadId }, { noRedirectOn401: true });
}

export function getEhpadsByEmail(email) {
    return API.get("/ehpads/by-email", { params: { email }, noRedirectOn401: true });
}

export function addEmailToEhpad(ehpadId, email) {
    return API.post(`/ehpads/${ehpadId}/emails/contact`, { email });
}

export function addEmailToEhpadAsFax(ehpadId, email) {
    return API.post(`/ehpads/${ehpadId}/emails/fax`, { email });
}

export function listMedicalEquipmentSuppliers(serviceKey) {
    return API.get(`/medical-equipment/services/${serviceKey}/suppliers`, { noRedirectOn401: true });
}

export function createMedicalEquipmentSupplier(serviceKey, data) {
    return API.post(`/medical-equipment/services/${serviceKey}/suppliers`, data, { noRedirectOn401: true });
}

export function updateMedicalEquipmentSupplier(serviceKey, supplierId, data) {
    return API.put(`/medical-equipment/services/${serviceKey}/suppliers/${supplierId}`, data, { noRedirectOn401: true });
}

export function deleteMedicalEquipmentSupplier(serviceKey, supplierId) {
    return API.delete(`/medical-equipment/services/${serviceKey}/suppliers/${supplierId}`, { noRedirectOn401: true });
}

export function sendReply(commandeId, sujet, contenu, extraRecipients, attachments, idempotencyKey) {
    const form = new FormData();
    form.append("sujet", sujet);
    form.append("contenu", contenu);
    if (extraRecipients) extraRecipients.forEach(r => form.append("extraRecipients", r));
    if (idempotencyKey) form.append("idempotencyKey", idempotencyKey);
    if (attachments) {
        attachments.forEach(f => form.append("attachments", f));
    }
    return API.post(`/commandes/${commandeId}/reply`, form, { noRedirectOn401: true });
}

export function createControleDossierLot(ehpadId, dateProduction, dossierType = null) {
    return API.get("/ehpads/controle-dossier-lot-create", {
        params: { ehpadId, dateProduction, ...(dossierType ? { dossierType } : {}) },
        noRedirectOn401: true,
    });
}

export function getControleDossiersLots(params = {}) {
    return API.get("/controle/dossiers-lots", { params, noRedirectOn401: true });
}

export function updateControleDossierLot(id, data) {
    return API.patch(`/controle/dossiers-lots/${id}`, data, { noRedirectOn401: true });
}

export function deleteControleDossierLot(id) {
    return API.delete(`/controle/dossiers-lots/${id}`, { noRedirectOn401: true });
}

export function uploadControleDossierLotAttachment(id, file) {
    const form = new FormData();
    form.append("file", file);
    return API.post(`/controle/dossiers-lots/${id}/pieces-jointes`, form, { noRedirectOn401: true });
}

export function deleteControleDossierLotAttachment(id, attachmentId) {
    return API.delete(`/controle/dossiers-lots/${id}/pieces-jointes/${attachmentId}`, { noRedirectOn401: true });
}

export function getControleDossierLotAttachmentContent(id, attachmentId) {
    return API.get(`/controle/dossiers-lots/${id}/pieces-jointes/${attachmentId}/content`, {
        responseType: "blob",
        noRedirectOn401: true,
    });
}

export function getCnoPatients() {
    return API.get("/cno/patients", { noRedirectOn401: true });
}

export function createCnoPatient(data) {
    return API.post("/cno/patients", data, { noRedirectOn401: true });
}

export function updateCnoPatient(id, data) {
    return API.patch(`/cno/patients/${id}`, data, { noRedirectOn401: true });
}

export function deleteCnoPatient(id) {
    return API.delete(`/cno/patients/${id}`, { noRedirectOn401: true });
}

export function createCnoLine(patientId, data) {
    return API.post(`/cno/patients/${patientId}/cnos`, data, { noRedirectOn401: true });
}

export function updateCnoLine(patientId, lineId, data) {
    return API.patch(`/cno/patients/${patientId}/cnos/${lineId}`, data, { noRedirectOn401: true });
}

export function deleteCnoLine(patientId, lineId) {
    return API.delete(`/cno/patients/${patientId}/cnos/${lineId}`, { noRedirectOn401: true });
}

export function createCnoDelivery(data) {
    return API.post("/cno/patients/deliveries", data, { noRedirectOn401: true });
}

export function getCnoEtiquetageLines() {
    return API.get("/cno/patients/etiquetage", { noRedirectOn401: true });
}

export function getCnoBonLivraisonLines() {
    return API.get("/cno/patients/bon-livraison", { noRedirectOn401: true });
}

export function updateCnoDeliveryLine(id, data) {
    return API.patch(`/cno/patients/delivery-lines/${id}`, data, { noRedirectOn401: true });
}

export function getRobotPlanningProductions() {
    return API.get("/robot/planning-productions", { noRedirectOn401: true });
}

export function createRobotPlanningProduction(data) {
    return API.post("/robot/planning-productions", data, { noRedirectOn401: true });
}

export function updateRobotPlanningProduction(id, data) {
    return API.patch(`/robot/planning-productions/${id}`, data, { noRedirectOn401: true });
}

export function deleteRobotPlanningProduction(id) {
    return API.delete(`/robot/planning-productions/${id}`, { noRedirectOn401: true });
}

export function uploadAdministrationPatientDocument(patientId, file) {
    const form = new FormData();
    form.append("file", file);
    return API.post(`/administration/patients/${patientId}/documents`, form, { noRedirectOn401: true });
}

export function deleteAdministrationPatientDocument(patientId, documentId) {
    return API.delete(`/administration/patients/${patientId}/documents/${documentId}`, { noRedirectOn401: true });
}

export function getAdministrationPatientDocumentContent(patientId, documentId) {
    return API.get(`/administration/patients/${patientId}/documents/${documentId}/content`, {
        responseType: "blob",
        noRedirectOn401: true,
    });
}

export function listComplianceDocuments(folderKey) {
    return API.get(`/conformite/folders/${folderKey}/documents`, { noRedirectOn401: true });
}

export function uploadComplianceDocument(folderKey, file, documentKey = null) {
    const form = new FormData();
    form.append("file", file);
    if (documentKey) form.append("documentKey", documentKey);
    return API.post(`/conformite/folders/${folderKey}/documents`, form, { noRedirectOn401: true });
}

export function addComplianceLink(folderKey, { documentKey = null, label = "", url }) {
    return API.post(`/conformite/folders/${folderKey}/links`, { documentKey, label, url }, { noRedirectOn401: true });
}

export function deleteComplianceDocument(folderKey, documentId) {
    return API.delete(`/conformite/folders/${folderKey}/documents/${documentId}`, { noRedirectOn401: true });
}

export function renameComplianceDocument(folderKey, documentId, fileName) {
    return API.patch(`/conformite/folders/${folderKey}/documents/${documentId}`, { fileName }, { noRedirectOn401: true });
}

export function getComplianceDocumentContent(folderKey, documentId) {
    return API.get(`/conformite/folders/${folderKey}/documents/${documentId}/content`, {
        responseType: "blob",
        noRedirectOn401: true,
    });
}
